import { DatePipe } from '@angular/common';
import { Component, input, computed } from '@angular/core';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { RouterModule } from '@angular/router';
import { ByProductItem, HaulierItem, SlaughterhouseItem, ServiceProductItem, EndProductItem } from '@hjc/masterdata-types';
import { TranslateModule } from '@ngx-translate/core';
import { OtherIntegrationsInfoComponent } from '@components/info/other-integrations-info/other-integrations-info.component';
import { InfoBlockComponent } from './info-block/info-block.component';

export type PricingType = 'logistics' | 'slaughterhouse' | 'end-customer' | 'carcass-collection' | 'service' | 'byproduct' | 'endproduct';

export type InfoField = {
  key: string;
  labelKey: string;
  type?: 'date' | 'string';
  pipe?: 'date';
  dateFormat?: string;
};

export type InfoItem = HaulierItem | SlaughterhouseItem | ByProductItem | ServiceProductItem | EndProductItem;

@Component({
  selector: 'md-info',
  imports: [InfoBlockComponent, MatDividerModule, RouterModule, TranslateModule, MatIconModule, OtherIntegrationsInfoComponent],
  templateUrl: './info.component.html',
  styleUrl: './info.component.scss',
  standalone: true
})
export class InfoComponent {
  item = input<InfoItem | undefined>(undefined);
  fields = input<InfoField[]>([]);
  pricingRouteLabel = input<string>('');
  pricingType = input<PricingType>();

  // Computed property for pricing route
  pricingRoute = computed(() => {
    const currentItem = this.item();
    const routeLabel = this.pricingRouteLabel();
    const pricingType = this.pricingType();

    if (!currentItem?.id || !routeLabel || !pricingType) {
      return null;
    }

    // Determine route based on pricing type
    switch (pricingType) {
      case 'logistics':
        return ['/app/pricing/partners/logistics', currentItem.id];
      case 'slaughterhouse':
        return ['/app/pricing/partners/slaughterhouses', currentItem.id];
      case 'end-customer':
        return ['/app/pricing/customers/end', currentItem.id];
      case 'carcass-collection':
        return ['/app/pricing/customers/carcass-collection', currentItem.id];
      case 'service':
        return ['/app/pricing/services', currentItem.id];
      case 'byproduct':
        return ['/app/pricing/products/byproducts', currentItem.id];
      default:
        return null;
    }
  });

  getFieldValue(field: InfoField): string | null {
    const currentItem = this.item();
    if (!currentItem) return null;

    // Get the value from the item using the field key
    const value = this.getNestedProperty(currentItem, field.key);

    if (value === null || value === undefined) return null;

    // Apply formatting based on field type
    if (field.type === 'date' && field.pipe === 'date') {
      const dateFormat = field.dateFormat || 'dd.MM.yyyy HH:mm';
      const datePipe = new DatePipe('en-US');

      // Ensure value is a valid date type
      if (value instanceof Date || typeof value === 'string' || typeof value === 'number') {
        return datePipe.transform(value, dateFormat);
      }

      return null;
    }

    return String(value);
  }

  private getNestedProperty(obj: InfoItem, path: string): unknown {
    return path
      .split('.')
      .reduce(
        (current: unknown, key: string) => (current && typeof current === 'object' && key in current ? (current as Record<string, unknown>)[key] : undefined),
        obj
      );
  }
}
